/**
 * /settings/assessments — evaluation / temperament day gate (migration 0099).
 *
 * Reads GET /api/assessments/settings; saves via PUT (capability
 * facilities.configure). "Create evaluation service" POSTs
 * /api/assessments/settings/service, which creates a category='assessment'
 * service and links it — a gate cannot be switched on until a service exists
 * (the API refuses, and the toggles stay disabled here).
 */
import {
  type AssessmentServiceSummary,
  type AssessmentSettingsView,
  createDefaultAssessmentService,
  getAssessmentSettings,
  updateAssessmentSettings,
} from '@/lib/assessments';
import { Loader2, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface ServiceOption {
  id: string;
  name: string;
  category: string;
  isActive: number | boolean;
}

const VALIDITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Never expires' },
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
];

export function AssessmentsSettings() {
  const [settings, setSettings] = useState<AssessmentSettingsView | null>(null);
  const [service, setService] = useState<AssessmentServiceSummary | null>(null);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [view, svcRes] = await Promise.all([
        getAssessmentSettings(),
        fetch('/api/services', { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : { services: [] }))
          .catch(() => ({ services: [] })),
      ]);
      setSettings(view.settings);
      setService(view.service);
      const list = (svcRes as { services?: ServiceOption[] }).services ?? [];
      setServices(list.filter((s) => s.category === 'assessment'));
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (p: Partial<AssessmentSettingsView>) =>
    setSettings((s) => (s ? { ...s, ...p } : s));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveError(null);
    try {
      const view = await updateAssessmentSettings({
        daycareRequiresAssessment: settings.daycareRequiresAssessment,
        boardingRequiresAssessment: settings.boardingRequiresAssessment,
        assessmentValidityMonths: settings.assessmentValidityMonths,
        assessmentServiceId: settings.assessmentServiceId,
        ownerMustStay: settings.ownerMustStay,
        assessmentNoteHtml: settings.assessmentNoteHtml,
      });
      setSettings(view.settings);
      setService(view.service);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const createService = async () => {
    setCreating(true);
    setSaveError(null);
    try {
      const view = await createDefaultAssessmentService({});
      setSettings(view.settings);
      setService(view.service);
      const created = view.service;
      if (created) {
        setServices((list) =>
          list.some((s) => s.id === created.id)
            ? list
            : [
                ...list,
                {
                  id: created.id,
                  name: created.name,
                  category: created.category,
                  isActive: created.isActive,
                },
              ],
        );
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not create the service.');
    } finally {
      setCreating(false);
    }
  };

  if (loadError) {
    return (
      <div className="text-sm text-destructive" role="alert">
        {loadError}
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
        Loading…
      </div>
    );
  }

  const hasService = settings.assessmentServiceId !== null;

  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <h1 className="text-xl font-semibold">Evaluation days</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Require a passed evaluation (temperament day) before a pet can be booked into daycare or
          boarding. Customers book the evaluation from the portal; staff record the outcome from the
          booking.
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-medium">Evaluation service</h2>
        <p className="text-xs text-muted-foreground">
          The service customers book for the evaluation. Price, length and name are editable on the
          Services page.
        </p>
        {services.length > 0 ? (
          <select
            aria-label="Evaluation service"
            value={settings.assessmentServiceId ?? ''}
            onChange={(e) => patch({ assessmentServiceId: e.currentTarget.value || null })}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">Not set</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="no-assessment-service">
            No evaluation service yet.
          </p>
        )}
        {service && (
          <p className="text-xs text-muted-foreground">
            Linked: <span className="font-medium text-foreground">{service.name}</span>
            {service.defaultDurationMin != null && (
              <>
                {' · '}
                <span className="num">{service.defaultDurationMin}</span> min
              </>
            )}
          </p>
        )}
        <button
          type="button"
          onClick={() => void createService()}
          disabled={creating}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          {creating ? (
            <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
          ) : (
            <Plus className="size-4" strokeWidth={1.75} />
          )}
          Create evaluation service
        </button>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium">Where it's required</h2>
        {!hasService && (
          <p className="text-xs text-warning-foreground">
            Choose or create an evaluation service before switching a gate on.
          </p>
        )}
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={settings.daycareRequiresAssessment}
            disabled={!hasService}
            onChange={(e) => patch({ daycareRequiresAssessment: e.currentTarget.checked })}
          />
          Daycare needs a passed evaluation
        </label>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={settings.boardingRequiresAssessment}
            disabled={!hasService}
            onChange={(e) => patch({ boardingRequiresAssessment: e.currentTarget.checked })}
          />
          Boarding needs a passed evaluation
        </label>
        <p className="text-xs text-muted-foreground">
          Switching a gate on applies to every pet on file, including regulars. Staff can
          grandfather a pet by recording a pass from the pet record, or override the gate on a
          booking.
        </p>
      </section>

      <section className="space-y-3">
        <label htmlFor="assessment-validity" className="text-sm font-medium">
          A pass is valid for
        </label>
        <select
          id="assessment-validity"
          value={
            settings.assessmentValidityMonths === null
              ? ''
              : String(settings.assessmentValidityMonths)
          }
          onChange={(e) =>
            patch({
              assessmentValidityMonths: e.currentTarget.value
                ? Number(e.currentTarget.value)
                : null,
            })
          }
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          {VALIDITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={settings.ownerMustStay}
            onChange={(e) => patch({ ownerMustStay: e.currentTarget.checked })}
          />
          The owner must stay for the evaluation
        </label>
      </section>

      <section>
        <label htmlFor="assessment-note" className="text-sm font-medium">
          What customers are told
        </label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Shown on the customer's pet page and in the confirmation email. Basic HTML is allowed.
        </p>
        <textarea
          id="assessment-note"
          value={settings.assessmentNoteHtml ?? ''}
          onChange={(e) => patch({ assessmentNoteHtml: e.currentTarget.value || null })}
          rows={5}
          placeholder="<p>Bring your dog on a lead and plan to stay for around 30 minutes...</p>"
          className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
        />
      </section>

      {saveError && (
        <p className="text-sm text-destructive" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {saving && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Save changes
        </button>
        {savedAt && !saving && (
          <span className="text-xs text-muted-foreground" data-testid="assessments-saved">
            Saved
          </span>
        )}
      </div>
    </div>
  );
}
